/**
 * OKRPerformanceTable Component
 * Table showing each OKR's contribution to performance score
 */

import React from 'react';
import { clsx } from 'clsx';
import { Card } from '../common';
import { Target, CheckCircle, ExternalLink } from 'lucide-react';
import type { OKRPerformanceItem } from '../../types';
import { getScoreColor } from '../../types';
import { okrStatusLabel } from '../../utils/okrStatus';
import { STATUS_BADGES } from './okrStatusBadges';

export interface OKRPerformanceTableProps {
  okrs: OKRPerformanceItem[];
  title?: string;
  onOKRClick?: (okrId: string) => void;
  className?: string;
  isLoading?: boolean;
}

const TYPE_BADGES: Record<string, { label: string; className: string }> = {
  company: { label: 'Company', className: 'bg-purple-100 text-purple-700' },
  department: { label: 'Department', className: 'bg-blue-100 text-blue-700' },
  team: { label: 'Team', className: 'bg-teal-100 text-teal-700' },
  individual: { label: 'Individual', className: 'bg-stone-100 text-stone-700' },
};

export const OKRPerformanceTable: React.FC<OKRPerformanceTableProps> = ({
  okrs,
  title = 'OKR Performance',
  onOKRClick,
  className,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="animate-pulse">
          <div className="h-5 bg-stone-200 rounded w-36 mb-6" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4 py-4 border-b border-stone-100">
              <div className="h-10 w-10 bg-stone-200 rounded-lg" />
              <div className="flex-1">
                <div className="h-4 bg-stone-200 rounded w-3/4 mb-2" />
                <div className="h-3 bg-stone-200 rounded w-1/2" />
              </div>
              <div className="h-6 bg-stone-200 rounded w-16" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (!okrs || okrs.length === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-800 mb-4">{title}</h3>
        <div className="text-center py-8 text-stone-400">
          <Target className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No OKRs found for this period</p>
        </div>
      </Card>
    );
  }

  // Sort by contribution (highest first)
  const sortedOKRs = [...okrs].sort((a, b) => b.contribution - a.contribution);

  return (
    <Card className={clsx('p-6', className)}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-stone-800">{title}</h3>
        <span className="text-sm text-stone-500">{okrs.length} OKRs</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200">
              <th className="text-left py-3 px-2 text-sm font-medium text-stone-500">
                Objective
              </th>
              <th className="text-center py-3 px-2 text-sm font-medium text-stone-500 w-24">
                Type
              </th>
              <th className="text-center py-3 px-2 text-sm font-medium text-stone-500 w-24">
                Status
              </th>
              <th className="text-center py-3 px-2 text-sm font-medium text-stone-500 w-24">
                Progress
              </th>
              <th className="text-center py-3 px-2 text-sm font-medium text-stone-500 w-20">
                KRs
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedOKRs.map((okr) => {
              const typeBadge = TYPE_BADGES[okr.type] || TYPE_BADGES.individual;
              // An unmapped status used to fall back to STATUS_BADGES.active, so a *closed*
              // OKR rendered as a green "Active" badge. Fall back to neutral styling and the
              // status's own name instead — unstyled is recoverable, mislabelled is not.
              const statusBadge = STATUS_BADGES[okr.status] || {
                label: okrStatusLabel(okr.status),
                className: 'bg-stone-100 text-stone-600',
              };

              return (
                <tr
                  key={okr.okrId}
                  className={clsx(
                    'border-b border-stone-100 last:border-b-0',
                    onOKRClick && 'hover:bg-stone-50 cursor-pointer'
                  )}
                  onClick={() => onOKRClick?.(okr.okrId)}
                >
                  {/* Title */}
                  <td className="py-4 px-2">
                    <div className="flex items-start gap-3">
                      <div
                        className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: `${getScoreColor(okr.progress)}20`,
                        }}
                      >
                        <Target
                          className="w-5 h-5"
                          style={{ color: getScoreColor(okr.progress) }}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-stone-800 truncate">
                          {okr.title}
                        </p>
                        <p className="text-xs text-stone-500">
                          Contributes {(okr.contribution * 100).toFixed(1)}% to score
                        </p>
                      </div>
                      {onOKRClick && (
                        <ExternalLink className="w-4 h-4 text-stone-400 flex-shrink-0" />
                      )}
                    </div>
                  </td>

                  {/* Type */}
                  <td className="py-4 px-2 text-center">
                    <span
                      className={clsx(
                        'inline-block px-2 py-1 rounded text-xs font-medium',
                        typeBadge.className
                      )}
                    >
                      {typeBadge.label}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="py-4 px-2 text-center">
                    <span
                      className={clsx(
                        'inline-block px-2 py-1 rounded text-xs font-medium',
                        statusBadge.className
                      )}
                    >
                      {statusBadge.label}
                    </span>
                  </td>

                  {/* Progress */}
                  <td className="py-4 px-2">
                    <div className="flex flex-col items-center gap-1">
                      <span
                        className="text-lg font-bold"
                        style={{ color: getScoreColor(okr.progress) }}
                      >
                        {Math.round(okr.progress)}%
                      </span>
                      <div className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${okr.progress}%`,
                            backgroundColor: getScoreColor(okr.progress),
                          }}
                        />
                      </div>
                    </div>
                  </td>

                  {/* Key Results */}
                  <td className="py-4 px-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-stone-600">
                        {okr.keyResultsCompleted}/{okr.keyResultsCount}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default OKRPerformanceTable;
